import {
  CalendarIcon,
  FileIcon,
  HashIcon,
  LinkIcon,
  ListOrderedIcon,
  MapPinIcon,
  NewspaperIcon,
  SquareCheckIcon,
  SquarePlayIcon,
  TagIcon,
  TypeIcon,
} from 'lucide-react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { z } from 'zod'
import { useForm } from 'react-hook-form'

import { DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'

export const Fields = {
  id: {
    value: 'id',
    label: 'ID',
    description: 'ID, primary key',
    icon: <ListOrderedIcon className="h-4 w-4" />,
  },
  text: {
    value: 'text',
    label: 'Text',
    description: 'Titles, paragraphs',
    icon: <TypeIcon className="h-4 w-4" />,
  },
  rich_text: {
    value: 'rich_text',
    label: 'Article',
    description: 'Build privately',
    icon: <NewspaperIcon className="h-4 w-4" />,
  },
  web_url: {
    value: 'web_url',
    label: 'URL',
    description: 'Links',
    icon: <LinkIcon className="h-4 w-4" />,
  },
  number: {
    value: 'number',
    label: 'Number',
    description: 'ID,rating,order number',
    icon: <HashIcon className="h-4 w-4" />,
  },
  checkbox: {
    value: 'checkbox',
    label: 'Checkbox',
    description: 'Yes or no, true or false',
    icon: <SquareCheckIcon className="h-4 w-4" />,
  },
  tag: {
    value: 'tag',
    label: 'Tag',
    description: 'Tagging items, filters',
    icon: <TagIcon className="h-4 w-4" />,
  },
  media_url: {
    value: 'media_url',
    label: 'Media',
    description: 'Upload images, videos, or audio files',
    icon: <SquarePlayIcon className="h-4 w-4" />,
  },
  file_url: {
    value: 'file_url',
    label: 'Files',
    description: 'Upload other files',
    icon: <FileIcon className="h-4 w-4" />,
  },
  timestamp: {
    value: 'timestamp',
    label: 'Date',
    description: 'Date of event, date added',
    icon: <CalendarIcon className="h-4 w-4" />,
  },
  address: {
    value: 'address',
    label: 'Address',
    description: 'Location',
    icon: <MapPinIcon className="h-4 w-4" />,
  },
}

const formSchema = z.object({
  type: z.string(),
  fieldName: z.string(),
})

export interface UpsertTableColumnProps {
  record?: any
  onUpsert?: (payload: any) => void
  onClose: () => void
}

export const UpsertTableColumnModal = ({
  record,
  onUpsert,
}: UpsertTableColumnProps) => {
  const t = useTranslations('database.form')
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: record
      ? {
          type: record.type,
          fieldName: record.fieldName,
        }
      : {
          type: 'text',
          fieldName: '',
        },
  })

  const { control, handleSubmit } = form

  const handleSave = (values: z.infer<typeof formSchema>) => {
    if (record) {
      onUpsert?.({
        type: 'alter',
        dataType: values.type,
        columnName: values.fieldName,
        oldColumnName: record.fieldName,
        oldDataType: record.type,
      })
    } else {
      onUpsert?.({
        type: 'add',
        dataType: values.type,
        columnName: values.fieldName,
      })
    }
  }

  return (
    <DialogContent className="container mx-auto flex flex-col max-w-xl bg-modal-gradient rounded-lg border-modal-border-color">
      <div className="flex items-center gap-2">
        <div className="text-[28px]">{record ? 'Edit' : 'Add'} Field</div>
      </div>

      <div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(handleSave)}
            className="space-y-4 text-base"
          >
            <FormField
              control={control}
              name="type"
              render={({ field }) => (
                <FormItem className="space-y-4">
                  <FormLabel>{t('field_type')}</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          {Fields[field.value as keyof typeof Fields]?.icon}
                          {Fields[field.value as keyof typeof Fields]?.label}
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-modal-gradient">
                        {Object.values(Fields).map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            <div className="flex items-center justify-between space-x-4">
                              <div className="flex items-center gap-1">
                                {f.icon}
                                <div>{f.label}</div>
                              </div>
                              <div className="text-sm text-muted-foreground ml-4">
                                {f.description}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                      <FormMessage />
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="fieldName"
              render={({ field }) => (
                <FormItem className="space-y-4 mt-4">
                  <FormLabel>{t('field_name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('enter_field_name')}
                      className="bg-new_ui-6 border border-transparent focus-visible:border-new_ui-20"
                      onChange={(e) => {
                        field.onChange(e)
                        form.clearErrors('fieldName')
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="w-full">
              <Button
                type="submit"
                size="custom"
                variant="ghost"
                className="w-full bg-white text-[#222] rounded-lg h-[40px] hover:bg-white hover:text-[#222]"
                // disabled={loading.crud.CREATE}
              >
                Save
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </DialogContent>
  )
}
